import { Dispatch, useState } from 'react';
import { EllaAction, EllaState } from '../Ella/Ella';

import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';

import { isValidVivrelleSku } from '@/util/regexp';
import { useCart } from '@/components/hook/cart';
import { Cart } from '@/types/Cart';
import { addItemToCart } from '@/util/cart_generic_client';
import analytics from '@/util/analytics';
import { useEllaAnalytics } from '@/components/hook/analytics';

type CartCtasProps = {
  className?: string;
  handleShowCart: () => void;
  setShouldShowReplaceItemInCartCTA: (shouldShow: boolean) => void;
  state: EllaState;
  dispatch: Dispatch<EllaAction>;
  productSelected?: any;
  setIsProcessing: (value: boolean) => void;
};

/**
 * When the user has a Vivrelle item in their cart, this component is used to handle the cart CTA.
 * This jouney happen when start the ella chat, or when is finalizing the checkout and can update the customer cart
 * @param props - The props for the component.
 * @returns The component.
 */
export function CartCtas(props: CartCtasProps) {
  const {
    className = '',
    handleShowCart,
    setShouldShowReplaceItemInCartCTA,
    dispatch,
    state,
    productSelected,
    setIsProcessing
  } = props;
  const { value: cartFromOutsideElla, actions: cartActions } = useCart();
  const { metadata } = useEllaAnalytics();
  const [disabledButton, setDisabledButton] = useState(false);

  // NOTE: the (item.borrowed === undefined) check is a bit of a janky way to get the Vivrelle item
  // in the cart that was added from within Ella, but it's good enough for now IMO
  const vivrelleItemSelectedWithinElla: any = state.cart.find(
    (item) => item.borrowed === undefined && isValidVivrelleSku(item.sku)
  );

  const handleUpdateCart = async () => {
    const itemToBeAdded = vivrelleItemSelectedWithinElla || productSelected;
    setDisabledButton(true);
    setIsProcessing(true);
    analytics.track('Ella AI Update Cart - Clicked', {
      ...metadata,
      cartValue: (cartFromOutsideElla as Cart).items.map((item) => {
        return {
          parentSku: item.sku,
          parentSkuTier: item.product.tier
        };
      })
    });

    // Remove items from the outside-Ella cart, that have the same tier as the newly-selected Ella Vivrelle item
    await removeItemFromCart(itemToBeAdded.tier);
    await addItemToCart(state.revolveCartId, itemToBeAdded);
    setShouldShowReplaceItemInCartCTA(false);
    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        cartItem: { ...itemToBeAdded, sku: itemToBeAdded.item_group_id }
      }
    });
    setDisabledButton(false);
    setIsProcessing(false);
  };

  // Generic function to remove items from the outside-Ella cart
  const removeItemFromCart = async (tier: string) => {
    await Promise.all(
      cartFromOutsideElla.items
        .filter(
          (item) => item.product.tier.toLowerCase() === tier.toLowerCase()
        )
        .map(async (item) => await cartActions.removeFromCart(item.id))
    );
  };

  return (
    <>
      <Text
        className={'content'}
        variant='text-s'
      >
        Looks like you have something in your Vivrelle cart - would you like to
        clear your cart and add {vivrelleItemSelectedWithinElla?.brand}{' '}
        {vivrelleItemSelectedWithinElla?.name} instead?
      </Text>

      <Button
        onClick={handleUpdateCart}
        variant='pink-400'
        disabled={disabledButton}
      >
        Yes, update cart
      </Button>

      <Button
        className='EllaMessages-product-btn-alt'
        disabled={disabledButton}
        onClick={() => {
          analytics.track('Ella AI Keep Item in Cart - Clicked', {
            ...metadata,
            cartValue: (cartFromOutsideElla as Cart).items.map((item) => {
              return {
                parentSku: item.sku,
                parentSkuTier: item.product.tier
              };
            })
          });
          setIsProcessing(false);
          setShouldShowReplaceItemInCartCTA(false);
        }}
      >
        No, keep my item
      </Button>
    </>
  );
}
